// Procedural ocean: Gerstner swells whose height follows the "storm" level, damped around the
// hull so the sea never washes over the deck, plus per-pixel ripples, sky reflection (Fresnel),
// sun glitter, light through the crests, broken whitecaps, white water along the hull and a wake
// behind the stern. `flow` scrolls the sea toward the stern (the ship sails bow-first, toward −x).
import { useFrame } from "@react-three/fiber";
import { useMemo, useRef } from "react";
import * as THREE from "three";

import { kDeepXZ } from "./coords";

const vert = /* glsl */ `
uniform float uTime;
uniform float uStorm;
uniform float uFlow;
uniform vec2 uAbyss;
uniform float uShip;
varying vec3 vWorld;
varying vec3 vNormalW;
varying float vCrest;
#include <fog_pars_vertex>

vec3 gerstner(vec4 wave, vec3 p, float damp, inout vec3 tangent, inout vec3 binormal) {
  float steep = wave.z * damp;
  float k = 6.28318 / wave.w;
  float c = sqrt(9.8 / k);
  vec2 d = normalize(wave.xy);
  float f = k * (dot(d, p.xz - vec2(uFlow, 0.0)) - c * uTime);
  float a = steep / k;
  tangent += vec3(-d.x * d.x * steep * sin(f), d.x * steep * cos(f), -d.x * d.y * steep * sin(f));
  binormal += vec3(-d.x * d.y * steep * sin(f), d.y * steep * cos(f), -d.y * d.y * steep * sin(f));
  return vec3(d.x * a * cos(f), a * sin(f), d.y * a * cos(f));
}

void main() {
  vec3 p = (modelMatrix * vec4(position, 1.0)).xyz;
  // Calm water right next to the hull.
  float hull = length(vec2(p.x / 13.5, p.z / 4.6));
  float damp = mix(1.0, mix(0.25, 1.0, smoothstep(1.0, 1.7, hull)), uShip) * mix(0.35, 1.0, uStorm);
  // The Deep's whirlpool sits on still water, so waves never poke through it.
  damp *= mix(0.03, 1.0, smoothstep(4.8, 7.5, length(p.xz - uAbyss)));
  vec3 tangent = vec3(1.0, 0.0, 0.0);
  vec3 binormal = vec3(0.0, 0.0, 1.0);
  vec3 q = p;
  q += gerstner(vec4(1.0, 0.25, 0.20, 31.0), p, damp, tangent, binormal);
  q += gerstner(vec4(0.7, 0.9, 0.16, 17.0), p, damp, tangent, binormal);
  q += gerstner(vec4(-0.3, 1.0, 0.13, 10.5), p, damp, tangent, binormal);
  q += gerstner(vec4(0.2, -1.0, 0.10, 12.5), p, damp, tangent, binormal);
  q += gerstner(vec4(0.9, -0.5, 0.08 + 0.12 * uStorm, 6.3), p, damp, tangent, binormal);
  q += gerstner(vec4(-0.8, 0.55, 0.05 + 0.08 * uStorm, 5.1), p, damp, tangent, binormal);
  vWorld = q;
  vNormalW = normalize(cross(binormal, tangent));
  vCrest = q.y;
  vec4 mvPosition = viewMatrix * vec4(q, 1.0);
  gl_Position = projectionMatrix * mvPosition;
  #include <fog_vertex>
}
`;

const frag = /* glsl */ `
uniform float uTime;
uniform float uStorm;
uniform float uFlow;
uniform float uSpeed;
uniform float uShip;
uniform vec3 uSunDir;
uniform vec3 uSunColor;
uniform vec3 uSkyColor;
uniform vec3 uDeep;
uniform vec3 uShallow;
uniform float uFlash;
varying vec3 vWorld;
varying vec3 vNormalW;
varying float vCrest;
#include <fog_pars_fragment>

float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float noise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x), mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
}
const mat2 kRot = mat2(0.8, -0.6, 0.6, 0.8);
/** Fractal noise, each octave rotated so the value-noise grid never shows. */
float fbm(vec2 p) {
  float a = 0.5, s = 0.0;
  for (int i = 0; i < 4; i++) { s += a * noise(p); p = kRot * p * 2.03 + 17.0; a *= 0.5; }
  return s / 0.9375;
}
/** Small wind ripples drifting over the swell. */
float ripples(vec2 p) {
  float t = uTime;
  return noise(p * 0.8 + vec2(t * 0.30, t * 0.17)) * 0.50
       + noise(kRot * p * 1.9 + vec2(-t * 0.45, t * 0.33)) * 0.30
       + noise(kRot * kRot * p * 4.3 + vec2(t * 0.62, -t * 0.55)) * 0.20;
}
/** Rough signed distance (world units) to the hull outline: pointed bow at −x, rounded stern at +x. */
float hullDist(vec2 p) {
  float w = p.x < -5.25 ? 3.43 * pow(clamp((p.x + 12.1) / 6.85, 0.0, 1.0), 0.75)
          : p.x > 8.75 ? 3.3 * sqrt(clamp((11.9 - p.x) / 3.15, 0.0, 1.0)) : 3.43;
  return max(abs(p.y) - w, max(-12.1 - p.x, p.x - 11.9));
}

void main() {
  float dist = length(cameraPosition - vWorld);
  vec2 pw = vWorld.xz - vec2(uFlow, 0.0);
  // Per-pixel ripple normals, faded out with distance (no shimmering far away).
  float near = smoothstep(70.0, 12.0, dist);
  float e = 0.12;
  float rx = ripples(pw + vec2(e, 0.0)) - ripples(pw - vec2(e, 0.0));
  float rz = ripples(pw + vec2(0.0, e)) - ripples(pw - vec2(0.0, e));
  float amp = mix(0.22, 0.45, uStorm) * near;
  vec3 n = normalize(normalize(vNormalW) + vec3(-rx, 0.0, -rz) * amp / (2.0 * e));

  vec3 v = normalize(cameraPosition - vWorld);
  float ndv = max(dot(n, v), 0.0);
  // Schlick Fresnel for water (F0 ~ 0.02): dark looking down, mirror-like at grazing angles.
  float fres = 0.02 + 0.98 * pow(1.0 - ndv, 5.0);
  vec3 r = reflect(-v, n);
  vec3 sky = mix(uSkyColor * 0.85, uSkyColor * 1.15 + vec3(0.02, 0.04, 0.08), clamp(r.y, 0.0, 1.0));

  // Water body: deeper colour in the troughs, lighter on the crests; drifting cloud shadows.
  float crestT = clamp(vCrest * 0.7 + 0.4, 0.0, 1.0);
  vec3 body = mix(uDeep, uShallow, crestT * 0.85);
  float diff = max(dot(n, uSunDir), 0.0);
  float cloud = mix(0.78, 1.0, smoothstep(0.35, 0.7, noise(vWorld.xz * 0.025 + vec2(uTime * 0.012, uTime * 0.007))));
  body *= (0.45 + 0.55 * diff) * cloud;

  // Light passing through the thin, backlit tops of the waves (turquoise glow).
  vec3 sunFlat = normalize(vec3(uSunDir.x, 0.0, uSunDir.z));
  float back = pow(max(dot(-v, sunFlat), 0.0), 2.0);
  float sss = back * smoothstep(-0.1, 0.9, vCrest) * (1.0 - 0.6 * uStorm);
  body += mix(uShallow, vec3(0.18, 0.75, 0.62), 0.5) * sss * 0.55;

  vec3 col = mix(body, sky, fres * 0.85);

  // Sun: sharp glitter plus a broad sheen; dimmed by storm clouds.
  vec3 h = normalize(uSunDir + v);
  float ndh = max(dot(n, h), 0.0);
  float sunVis = (1.0 - 0.85 * uStorm) * cloud;
  col += uSunColor * (pow(ndh, 900.0) * 6.0 + pow(ndh, 90.0) * 0.35) * sunVis;

  // Whitecaps: broken foam on the highest crests, more of it in a storm.
  // Lacy foam pattern: domain-warped fractal noise, streaked along the flow.
  vec2 fp = vec2(pw.x * 0.6, pw.y) * 2.4;
  fp += 0.9 * vec2(fbm(fp * 0.7 + uTime * 0.05), fbm(fp * 0.7 + 7.3 - uTime * 0.04));
  float lace = fbm(fp * 1.3 + vec2(uTime * 0.15, 0.0));
  float foamTex = mix(0.5, smoothstep(0.42, 0.68, lace), near);
  float capLine = 0.85 - 0.3 * uStorm;
  float caps = smoothstep(capLine, capLine + 0.35, vCrest + (noise(pw * 0.6) - 0.5) * 0.5) * foamTex;
  caps *= 0.3 + 0.4 * uStorm;
  // White water hugging the hull and a wake fanning out behind the stern.
  float hd = hullDist(vWorld.xz);
  float churn = mix(0.5, lace, near);
  float hullFoam = smoothstep(1.0, 0.0, hd) * smoothstep(-0.4, 0.05, hd) * smoothstep(0.38 - 0.3 * smoothstep(0.5, 0.0, hd), 0.7, churn);
  float wx = vWorld.x - 11.0;
  float wakeW = 1.4 + wx * 0.22;
  float wake = step(0.0, wx) * smoothstep(wakeW, wakeW * 0.3, abs(vWorld.z)) * smoothstep(30.0 + 25.0 * uSpeed, 2.0, wx)
             * smoothstep(0.4, 0.7, churn) * clamp(0.5 + uSpeed, 0.0, 1.6);
  float foam = clamp(caps + (hullFoam * 0.65 + wake * 0.6) * uShip, 0.0, 1.0);
  vec3 foamCol = vec3(0.86, 0.9, 0.9) * (0.55 + 0.45 * diff) * mix(1.0, 0.6, uStorm);
  col = mix(col, foamCol, foam);

  col += vec3(0.7, 0.75, 0.95) * uFlash;
  gl_FragColor = vec4(col, 1.0);
  #include <fog_fragment>
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

export interface OceanLook {
  storm: number;
  deep: THREE.Color;
  shallow: THREE.Color;
  sky: THREE.Color;
  sunColor: THREE.Color;
  sunDir: THREE.Vector3;
}

export function Ocean({ look, flowRef, flashRef, shipRef }: {
  look: OceanLook;
  /** 1 while the ship floats, fading to 0 as it sinks (calm water, hull foam and wake go with it). */
  shipRef: React.MutableRefObject<number>;
  /** Extra stern-ward flow speed (set by the "ship surges forward" effect). */
  flowRef: React.MutableRefObject<number>;
  flashRef: React.MutableRefObject<number>;
}) {
  const mat = useRef<THREE.ShaderMaterial>(null);
  const uniforms = useMemo(() => THREE.UniformsUtils.merge([
    THREE.UniformsLib.fog,
    {
      uTime: { value: 0 }, uStorm: { value: look.storm }, uFlow: { value: 0 }, uSpeed: { value: 0 }, uShip: { value: 1 }, uFlash: { value: 0 },
      uAbyss: { value: new THREE.Vector2(kDeepXZ[0], kDeepXZ[1]) },
      uSunDir: { value: look.sunDir.clone() }, uSunColor: { value: look.sunColor.clone() }, uSkyColor: { value: look.sky.clone() },
      uDeep: { value: look.deep.clone() }, uShallow: { value: look.shallow.clone() },
    },
  ]), []); // eslint-disable-line react-hooks/exhaustive-deps
  const flow = useRef(0);
  useFrame((_, dt) => {
    const m = mat.current;
    if (!m) return;
    const u = m.uniforms;
    u.uTime.value += dt;
    flow.current += dt * (0.6 + flowRef.current);
    u.uFlow.value = flow.current;
    u.uSpeed.value = flowRef.current;
    u.uShip.value = shipRef.current;
    u.uFlash.value = flashRef.current;
    // Ease the look toward its target so weather changes roll in.
    const k = Math.min(1, dt * 0.8);
    u.uStorm.value += (look.storm - u.uStorm.value) * k;
    (u.uDeep.value as THREE.Color).lerp(look.deep, k);
    (u.uShallow.value as THREE.Color).lerp(look.shallow, k);
    (u.uSkyColor.value as THREE.Color).lerp(look.sky, k);
    (u.uSunColor.value as THREE.Color).lerp(look.sunColor, k);
    (u.uSunDir.value as THREE.Vector3).lerp(look.sunDir, k).normalize();
  });
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} frustumCulled={false}>
      <planeGeometry args={[320, 320, 260, 260]} />
      <shaderMaterial ref={mat} vertexShader={vert} fragmentShader={frag} uniforms={uniforms} fog />
    </mesh>
  );
}
